import { create } from 'zustand'
import { User } from '@shared/types'

// Keys for localStorage persistence (mock until real API)
const ACCOUNTS_STORAGE_KEY = 'adam_tiktok_accounts'
const LAST_ACTIVE_ACCOUNT_KEY = 'adam_last_active_account'


export interface TikTokAccount {
    id: string;
    username: string;
    displayName?: string;
    avatarUrl: string;
    isActive: boolean;
    role?: 'owner' | 'manager';
    stats?: {
        followers: number;
        likes: number;
        views: number;
        videos: number;
    };
}

interface AuthState {
    user: User | null
    accounts: TikTokAccount[]
    accountsLoaded: boolean
    isLoading: boolean
    error: string | null
    setUser: (user: User | null) => void
    setAccounts: (accounts: TikTokAccount[]) => void
    loadAccounts: () => void
    setActiveAccount: (id: string) => void
    setLoading: (loading: boolean) => void
    setError: (error: string | null) => void
    logout: () => void
}


/**
 * Global authentication state using Zustand for reactive updates.
 * Manages user session and TikTok accounts across all dashboard components.
 * Accounts are persisted to localStorage until real TikTok OAuth API is implemented.
 */

export const useAuthStore = create<AuthState>((set) => ({
    user: null,
    accounts: [],
    accountsLoaded: false,
    isLoading: true,
    error: null,
    setUser: (user) => set({ user, isLoading: false }),
    
    setAccounts: (accounts) => {
        // Persist to localStorage (mock persistence until real API)
        localStorage.setItem(ACCOUNTS_STORAGE_KEY, JSON.stringify(accounts))
        
        // If there's a last active account, restore it
        const lastActiveId = localStorage.getItem(LAST_ACTIVE_ACCOUNT_KEY)
        const updatedAccounts = accounts.map(acc => ({
            ...acc,
            isActive: acc.id === lastActiveId || (lastActiveId === null && accounts.indexOf(acc) === 0)
        }))
        
        set({ accounts: updatedAccounts, accountsLoaded: true })
    },
    
    loadAccounts: () => {
        // Load from localStorage on app startup
        const stored = localStorage.getItem(ACCOUNTS_STORAGE_KEY)
        if (stored) {
            try {
                const accounts = JSON.parse(stored) as TikTokAccount[]
                const lastActiveId = localStorage.getItem(LAST_ACTIVE_ACCOUNT_KEY)
                const updatedAccounts = accounts.map(acc => ({
                    ...acc,
                    isActive: acc.id === lastActiveId || (lastActiveId === null && accounts.indexOf(acc) === 0)
                }))
                set({ accounts: updatedAccounts, accountsLoaded: true })
            } catch {
                set({ accounts: [], accountsLoaded: true })
            }
        } else {
            set({ accountsLoaded: true })
        }
    },
    
    setActiveAccount: (id) => {
        // Persist last active account to localStorage
        // This allows cookie-based persistence across tabs
        localStorage.setItem(LAST_ACTIVE_ACCOUNT_KEY, id)
        
        set((state) => ({
            accounts: state.accounts.map(acc => ({
                ...acc,
                isActive: acc.id === id
            }))
        }))
    },
    
    setLoading: (isLoading) => set({ isLoading }),
    setError: (error) => set({ error }),
    
    logout: () => {
        // Clear persisted accounts on logout
        localStorage.removeItem(ACCOUNTS_STORAGE_KEY)
        localStorage.removeItem(LAST_ACTIVE_ACCOUNT_KEY)
        set({ user: null, accounts: [], accountsLoaded: false, error: null })
    },
}))
